import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { createProject } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import '../../../../styles/wizard.css';

const StepM3ProjectSetup = () => {
  const navigate = useNavigate();
  const { project, migrate, setProject, setMigrate, nextStep, previousStep } = useOnboarding();
  
  const [formData, setFormData] = useState({
    projectName: migrate.projectName || project.name || '',
    framework: migrate.framework || '',
    language: migrate.language || '',
    repoType: migrate.repoType || 'tests_only'
  });
  
  const [loading, setLoading] = useState(false);

  const frameworks = [
    { value: 'selenium', label: 'Selenium (Java/Python/JS/C#)' },
    { value: 'cypress', label: 'Cypress' },
    { value: 'playwright', label: 'Playwright' },
    { value: 'robot', label: 'Robot Framework' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContinue = async () => {
    if (!formData.projectName || !formData.framework) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      // Create project with migration intent
      const response = await createProject({
        name: formData.projectName,
        intent: 'MIGRATE_SCRIPTS',
        framework_hint: formData.framework,
        language_hint: formData.language || 'auto-detect',
        repo_type: formData.repoType
      });

      if (response.success) {
        // Save project details
        setProject({
          name: formData.projectName,
          projectId: response.data.project_id,
          applicationType: 'web' // Default for migrated scripts
        });

        // Save migration-specific details
        setMigrate({
          projectName: formData.projectName,
          framework: formData.framework,
          language: formData.language,
          repoType: formData.repoType
        });

        toast.success('Project created successfully!');
        nextStep();
        navigate('/app/onboarding?step=4');
      } else {
        throw new Error('Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={3} completedSteps={[1, 2]} />
        
        <WizardCard
          title="Create your migration project"
          subtitle="Configure project details for your migrated automation scripts"
        >
          <form onSubmit={(e) => { e.preventDefault(); handleContinue(); }}>
            {/* Project Name */}
            <div className="form-group">
              <label className="form-label">
                Project Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="e.g., MyApp Test Automation"
                className="form-control"
                required
              />
            </div>

            {/* Framework */}
            <div className="form-group">
              <label className="form-label">
                Automation Framework <span className="text-danger">*</span>
              </label>
              <select
                name="framework"
                value={formData.framework}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="">Select Framework</option>
                {frameworks.map(fw => (
                  <option key={fw.value} value={fw.value}>
                    {fw.label}
                  </option>
                ))}
              </select>
              <small className="form-help">
                We'll auto-detect the exact version when you upload scripts
              </small>
            </div>

            {/* Language (Optional) */}
            <div className="form-group">
              <label className="form-label">
                Programming Language (Optional)
              </label>
              <input
                type="text"
                name="language"
                value={formData.language}
                onChange={handleChange}
                placeholder="e.g., Python, JavaScript, Java (or leave empty for auto-detect)"
                className="form-control"
              />
              <small className="form-help">
                Leave empty to auto-detect from uploaded code
              </small>
            </div>

            {/* Repo Type */}
            <div className="form-group">
              <label className="form-label">
                Repository Type
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <label style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1.5rem 1rem',
                  border: '2px solid',
                  borderColor: formData.repoType === 'tests_only' ? 'var(--primary-color)' : 'var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: formData.repoType === 'tests_only' ? 'rgba(76, 201, 240, 0.05)' : 'white',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <input
                    type="radio"
                    name="repoType"
                    value="tests_only"
                    checked={formData.repoType === 'tests_only'}
                    onChange={handleChange}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📦</div>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '1rem' }}>
                      Tests Only
                    </div>
                    <small style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      Just test files and dependencies
                    </small>
                  </div>
                </label>

                <label style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1.5rem 1rem',
                  border: '2px solid',
                  borderColor: formData.repoType === 'full_repo' ? 'var(--primary-color)' : 'var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: formData.repoType === 'full_repo' ? 'rgba(76, 201, 240, 0.05)' : 'white',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <input
                    type="radio"
                    name="repoType"
                    value="full_repo"
                    checked={formData.repoType === 'full_repo'}
                    onChange={handleChange}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🗂️</div>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '1rem' }}>
                      Full Repository
                    </div>
                    <small style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      Complete codebase with tests
                    </small>
                  </div>
                </label>
              </div>
            </div>

            <WizardActions
              showBack={true}
              onBack={() => {
                previousStep();
                navigate('/app/onboarding?step=2');
              }}
              showNext={true}
              onNext={handleContinue}
              nextLabel={loading ? 'Creating...' : 'Continue'}
              nextDisabled={loading || !formData.projectName || !formData.framework}
            />
          </form>
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default StepM3ProjectSetup;